import type { FC, ReactNode } from 'react';
import { ShieldAlert, ShieldCheck, ShieldQuestion, ShieldX } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

import { arr, num, optStr, str } from '@/app/lib/a2ui/props.ts';

/**
 * Shared parts of Shivurt's procurement artifacts (brief, shortlist,
 * comparison, request). Not registered as surfaces — internal building blocks so
 * the four artifacts read as one document family.
 */

const messages = defineMessages({
  tierUnknown: {
    id: 'procurement.tier.unknown',
    defaultMessage: 'Verification not stated',
    description: 'Badge shown when the supplier directory states no verification tier.',
  },
  verifiedOn: {
    id: 'procurement.tier.verifiedOn',
    defaultMessage: 'Verified {date}',
    description: 'Caption showing when a supplier was verified.',
  },
  score: {
    id: 'procurement.score',
    defaultMessage: 'Match {score}/100',
    description: 'Weighted match score for a supplier against the brief.',
  },
});

/** The four Shivurt tiers plus the honest fallback for a directory silence. */
export type TierKey =
  | 'verified_plus'
  | 'verified'
  | 'documentation_pending'
  | 'unverified'
  | 'unknown';

const TIER_KEYS: TierKey[] = [
  'verified_plus',
  'verified',
  'documentation_pending',
  'unverified',
  'unknown',
];

export function readTier(value: unknown): TierKey {
  const text = typeof value === 'string' ? value.trim() : '';
  return (TIER_KEYS as string[]).includes(text) ? (text as TierKey) : 'unknown';
}

/**
 * Tier styling is a signal, not decoration: Verified+ green, Verified blue,
 * Documentation Pending amber, Unverified and unstated red. A buyer must be able
 * to read the risk from across the room, and an unstated tier must never look
 * like a verified one. Each colour is a meaning token that keeps its hue in both
 * appearances — only its lightness moves, so it stays legible on either ground.
 */
const TIER_STYLES: Record<TierKey, { className: string; Icon: typeof ShieldCheck }> = {
  verified_plus: { className: 'bg-success/12 text-success', Icon: ShieldCheck },
  verified: { className: 'bg-verified/12 text-verified', Icon: ShieldCheck },
  documentation_pending: { className: 'bg-warning/14 text-warning', Icon: ShieldAlert },
  unverified: { className: 'bg-destructive/10 text-destructive', Icon: ShieldX },
  unknown: { className: 'bg-destructive/10 text-destructive', Icon: ShieldQuestion },
};

export interface SupplierHeader {
  id: string;
  name: string;
  tier: TierKey;
  tierLabel: string;
  verifiedOn?: string;
  score?: number;
}

export function readSupplierHeaders(value: unknown): SupplierHeader[] {
  return arr(value)
    .filter(
      (entry): entry is Record<string, unknown> =>
        typeof entry === 'object' && entry !== null && !Array.isArray(entry),
    )
    .map((entry, index) => {
      const tier = readTier(entry.tier);
      const verifiedOn = optStr(entry.verifiedOn);
      const score = num(entry.score);
      return {
        id: str(entry.id) || `supplier-${index}`,
        name: str(entry.name),
        tier,
        tierLabel: optStr(entry.tierLabel) ?? '',
        ...(verifiedOn ? { verifiedOn } : {}),
        ...(score !== undefined ? { score } : {}),
      };
    })
    .filter((entry) => entry.name.length > 0);
}

export const VerificationBadge: FC<{ tier: TierKey; label?: string; verifiedOn?: string }> = ({
  tier,
  label,
  verifiedOn,
}) => {
  const intl = useIntl();
  // A tier string we do not recognise must degrade to the cautious "unstated" badge,
  // never take the whole shortlist down with it: an unreadable tier is a risk signal,
  // not a render error.
  const { className, Icon } = TIER_STYLES[tier] ?? TIER_STYLES.unknown;
  const text = label && label.length > 0 ? label : intl.formatMessage(messages.tierUnknown);
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-sm px-2 py-1 text-body-xs font-semibold ${className}`}
    >
      <Icon aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
      <span translate="no">{text}</span>
      {verifiedOn ? (
        <span className="font-normal opacity-80">
          · {intl.formatMessage(messages.verifiedOn, { date: verifiedOn })}
        </span>
      ) : null}
    </span>
  );
};

/** The weighted score, shown as a plain figure so it never reads as a rating. */
export const ScorePill: FC<{ score: number }> = ({ score }) => {
  const intl = useIntl();
  return (
    <span className="rounded-sm border border-border px-2 py-1 text-body-xs font-semibold text-muted-foreground">
      {intl.formatMessage(messages.score, { score })}
    </span>
  );
};

/** The eyebrow + title block that marks a formal artifact, not a chat reply. */
export const ArtifactHeader: FC<{
  kind: string;
  title: string;
  reference?: string;
  sourceNote?: string;
  status?: ReactNode;
}> = ({ kind, title, reference, sourceNote, status }) => (
  <header className="border-b border-border pb-4">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-body-xs font-semibold uppercase tracking-caps text-brand-accent-ink">
        {kind}
      </p>
      {status}
    </div>
    <h1 className="mt-2 text-balance font-display text-heading-lg font-bold tracking-tight text-foreground">
      {title}
    </h1>
    {reference ? (
      <p
        className="mt-1 text-body-xs uppercase tracking-caps text-muted-foreground-subtle"
        translate="no"
      >
        {reference}
      </p>
    ) : null}
    {sourceNote ? <p className="mt-2 text-body-sm text-muted-foreground">{sourceNote}</p> : null}
  </header>
);

export const ArtifactCard: FC<{ children: ReactNode }> = ({ children }) => (
  <section className="flex flex-col gap-5 rounded-xl border border-border bg-card p-6 shadow-elevated sm:p-8">
    {children}
  </section>
);

/**
 * The reference / prepared-by / date block that heads Shivurt's brief and its
 * request, so the two artifacts read as one paper trail.
 */
export const ArtifactMeta: FC<{ rows: { label: string; value: string }[] }> = ({ rows }) => {
  const shown = rows.filter((row) => row.value.length > 0);
  if (shown.length === 0) {
    return null;
  }
  return (
    <dl className="flex flex-col gap-1">
      {shown.map((row) => (
        <div key={row.label} className="flex flex-wrap gap-x-2 text-body-xs">
          <dt className="font-semibold uppercase tracking-caps text-muted-foreground-subtle">
            {row.label}
          </dt>
          <dd className="text-muted-foreground" translate="no">
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
};

export const PrimaryButton: FC<{ label: string; onClick: () => void; disabled?: boolean }> = ({
  label,
  onClick,
  disabled,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="touch-manipulation rounded-input bg-primary px-5 py-2.5 text-body-sm font-semibold text-primary-foreground transition-opacity duration-150 ease-out hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
  >
    {label}
  </button>
);

export const SecondaryButton: FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="touch-manipulation rounded-input border border-border bg-card px-5 py-2.5 text-body-sm font-semibold text-foreground transition-colors duration-150 ease-out hover:border-brand-accent hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary active:scale-[0.98]"
  >
    {label}
  </button>
);
